import React, { useState, useCallback, useEffect } from 'react'
import { Col, Form, Row, Collapse, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import LanguageNameInput from '../../Forms/LanguageNameInput'
import OverlayContainer from '../../Page/logix2020/OverlayContainer'
import OverlayRight from '../../Page/logix2020/OverlayRight'
import LanguageForm from '../../Forms/LanguageForm'
import InformationPopover from '../../InformationPopover/InformationPopover'
import ArraySelect from '../../Select/ArraySelect'
import { ArrowBigIcon } from '../../Icons/ArrowBigIcon'
import { StyledCollapseTrigger } from './formHelper'
import { colors } from '../../../styles/colors'
import { OverlaySpinnerWrapper } from '../../Forms/StyledInputLoadingOverlay'
import { useArticleDataState } from '../../../stores/Configuration/ArticleDataProvider'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import ArticleSelectModal from '../../ArticleSelect/ArticleSelectModal'
import { emptyGuid, isEmptyGuid } from '../../../utils/emptyGuid'
import { StyledValidationMessage } from './formHelper'
import FindIcon from '../../../assets/icons/find.png'
import { setValueByKey } from '../../../utils/utils'
import MultiLangEditorInput from '../../Forms/MultiLangEditorInput'

const StyledImage = styled.div`
  height: 24px;
  width: 24px;
  background-size: contain;
  background-image: none;
  background-image: url(${(props) => (props.image ? props.image : '')});
`
const ArticleSelect = ({
  setSelectedArticleId,
  handleArticleWasChanged,
  disabled,
}) => {
  const [showAddArticlesModal, setShowAddArticlesModal] = useState(false)
  const [selectedArticles, setSelectedArticles] = useState([])
  useEffect(() => {
    if (!selectedArticles.length) {
      setSelectedArticleId(emptyGuid)
    } else {
      setSelectedArticleId(selectedArticles[0].article.id)
    }
  }, [setSelectedArticleId, selectedArticles])
  return (
    <>
      <StyledImage
        style={{ cursor: 'pointer' }}
        image={FindIcon}
        onClick={() => !disabled && setShowAddArticlesModal(true)}
      />
      {showAddArticlesModal ? (
        <ArticleSelectModal
          selectedArticles={selectedArticles}
          setSelectedArticles={setSelectedArticles}
          setShowAddArticlesModal={setShowAddArticlesModal}
          showAddArticlesModal={showAddArticlesModal}
          handleSave={() => {
            setShowAddArticlesModal(false)
            handleArticleWasChanged()
          }}
          singleSelection={true}
          officeIndependent={true}
          localArticleDependent={true}
        />
      ) : null}
    </>
  )
}

const ArticleMainForm = ({ articleBaseObj, setArticleBaseObj }) => {
  const { t } = useTranslation()
  const [overlayInternalNameIsVisible, setOverlayInternalNameIsVisible] =
    useState(false)
  const [overlayExternalNameIsVisible, setOverlayExternalNameIsVisible] =
    useState(false)
  const [overlayDescriptionIsVisible, setOverlayDescriptionIsVisible] =
    useState(false)
  const [overlayCommntIsVisible, setOverlayCommentIsVisible] = useState(false)
  const [attributesIsOpened, setAttributesIsOpened] = useState(false)
  const [variousIsOpened, setVariousIsOpened] = useState(false)
  const [supplyChainIsOpened, setSupplyChainIsOpened] = useState(false)
  const [selectedFollowerArticleId, setSelectedFollowerArticleId] =
    useState(emptyGuid)
  const {
    actionMode,
    suppliers,
    getSuppliers,
    articleDetails,
    setArticleDetails,
    getArticleDetails,
    units,
    getUnits,
    getSupplierAlias,
    storehouses,
    getStorehouses,
    loadingStates,
    getFormErrorByKey,
    resetFormErrorByKey,
  } = useArticleDataState()
  const loadData = async () => {
    getSuppliers()
    getStorehouses()
    getUnits()
  }
  useMountEffect(() => {
    loadData()
  })
  useEffect(() => {
    setArticleBaseObj((prev) => ({
      ...prev,
      followerArticle: {
        ...prev.followerArticle,
        displayName: articleDetails?.displayName ?? '',
        articleNumber: articleDetails?.articleNumber ?? '',
        id: articleDetails?.id ?? emptyGuid,
      },
    }))
  }, [articleDetails, setArticleBaseObj])

  useEffect(() => {
    if (!isEmptyGuid(articleBaseObj.id) && actionMode !== 'copy') {
      getSupplierAlias(articleBaseObj.id, articleBaseObj.supplier.id)
    }
  }, [
    actionMode,
    articleBaseObj.id,
    articleBaseObj.supplier.id,
    getSupplierAlias,
  ])

  const handleArticleWasChanged = async () => {
    if (isEmptyGuid(selectedFollowerArticleId)) {
      setArticleDetails(null)
    } else if ((articleDetails?.id ?? emptyGuid) != selectedFollowerArticleId) {
      getArticleDetails(selectedFollowerArticleId)
    }
  }
  const handleClickCollapse = useCallback((setData) => {
    setData((prev) => !prev)
  }, [])
  const updateTranslations = (name) => (translations) => {
    setArticleBaseObj((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        values: translations,
      },
    }))
    resetFormError(name)
  }
  const updateInputFields = (e) => {
    const { target } = e
    let key = target.name
    let value = null
    switch (key) {
      case 'classisWithSizes':
        setArticleBaseObj((prev) => ({
          ...prev,
          class: {
            ...prev.class,
            isWithSizes: target.checked,
          },
        }))
        break
      case 'classunit':
        setArticleBaseObj((prev) => ({
          ...prev,
          class: {
            ...prev.class,
            unit: {
              ...prev.class.unit,
              id: target.value,
            },
          },
        }))
        break
      case 'packageSize':
        //const onlyNums = target.value.replace(/[^0-9]/g, "")
        setArticleBaseObj((prev) => ({
          ...prev,
          [key]: parseInt(target.value) || 0,
        }))
        break
      case 'contingentsCostperItem':
        setArticleBaseObj((prev) => ({
          ...prev,
          [key]: parseFloat(target.value) || 0,
        }))
        break
      case 'price':
        setArticleBaseObj((prev) => ({
          ...prev,
          price: {
            ...prev.price,
            value: parseFloat(target.value) || null,
          },
        }))
        break
      case 'storehouse':
      case 'supplier':
        setArticleBaseObj((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            id: isEmptyGuid(target.value) ? emptyGuid : target.value,
          },
        }))
        break
      default:
        if (target.type === 'checkbox') {
          value = target.checked
        } else {
          value = target.value
        }
        setArticleBaseObj((prev) => setValueByKey(key, value, prev))
        break
    }
    resetFormError(key)
  }
  const getFormError = (name) => {
    return getFormErrorByKey('saveArticle', name)
  }
  const resetFormError = (name) => {
    resetFormErrorByKey('saveArticle', name)
  }
  const foreignKeyIsEnabled = () => {
    return actionMode === 'copy' || !articleBaseObj.foreignKey
  }
  const handleOpenLanguageModal = (setData) => {
    handleCloseOverlay()
    setData(true)
  }
  const handleCloseOverlay = () => {
    setOverlayInternalNameIsVisible(false)
    setOverlayExternalNameIsVisible(false)
    setOverlayDescriptionIsVisible(false)
    setOverlayCommentIsVisible(false)
  }
  const renderBaseData = () => {
    return (
      <>
        <Row className='mb-3'>
          <Col className='mb-3' xs={12} sm={6} md={6}>
            <Form.Group>
              <Form.Label>{t('articleAdmin:articleNumber')}</Form.Label>
              <Form.Control
                id='number'
                name='number'
                placeholder={t('articleAdmin:articleNumber')}
                value={articleBaseObj.number}
                onChange={updateInputFields}
                isInvalid={!!getFormError('number')}
              />
              <Form.Control.Feedback type='invalid'>
                {getFormError('number')}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={6}>
            <Form.Group>
              <Form.Label>
                {t('costCenterAdmin:foreignKey')}
                {foreignKeyIsEnabled() && (
                  <InformationPopover
                    header={t('costCenterAdmin:foreignKey')}
                    content={t('officeAdmin:noteForeignKey')}
                  />
                )}
              </Form.Label>
              <Form.Control
                id='foreignKey'
                name='foreignKey'
                placeholder={t('costCenterAdmin:foreignKey')}
                value={articleBaseObj.foreignKey}
                disabled={!foreignKeyIsEnabled()}
                onChange={updateInputFields}
                isInvalid={!!getFormError('foreignKey')}
              />
              <Form.Control.Feedback type='invalid'>
                {getFormError('foreignKey')}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row className='mb-3'>
          <LanguageNameInput
            openLanguageModal={() =>
              handleOpenLanguageModal(setOverlayInternalNameIsVisible)
            }
            languageArray={articleBaseObj.internalName.values}
            onChange={updateTranslations('internalName')}
            label={t('articleAdminTable:articleName')}
            isInvalid={!!getFormError('internalName')}
          />
          {!!getFormError('internalName') && (
            <StyledValidationMessage>
              {getFormError('internalName')}
            </StyledValidationMessage>
          )}
        </Row>
        <Row className='mb-3'>
          <MultiLangEditorInput
            label={t('articleAdmin:description')}
            mode='richText'
            allowToggle={true}
            openLanguageModal={() =>
              handleOpenLanguageModal(setOverlayDescriptionIsVisible)
            }
            languageArray={articleBaseObj.description.values}
            onChange={updateTranslations('description')}
            isInvalid={!!getFormError('description')}
          />
          {!!getFormError('description') && (
            <StyledValidationMessage>
              {getFormError('description')}
            </StyledValidationMessage>
          )}
        </Row>
        <Row className='mb-3'>
          <LanguageNameInput
            inputType='textarea'
            openLanguageModal={() =>
              handleOpenLanguageModal(setOverlayCommentIsVisible)
            }
            languageArray={articleBaseObj.comment.values}
            onChange={updateTranslations('comment')}
            label={t('articleAdmin:comment')}
            isInvalid={!!getFormError('comment')}
          />
          {!!getFormError('comment') && (
            <StyledValidationMessage>
              {getFormError('comment')}
            </StyledValidationMessage>
          )}
        </Row>
      </>
    )
  }
  const renderFollowerArticle = () => {
    let followerArticleDisplayname = isEmptyGuid(
      articleBaseObj.followerArticle.id
    )
      ? ''
      : articleBaseObj.followerArticle.articleNumber +
      ' ' +
      articleBaseObj.followerArticle.displayName
    return (
      <Form.Group className='mb-3'>
        <Form.Label className='d-block'>
          {t('articleAdmin:followerArticle')}
        </Form.Label>
        <div className='d-flex align-items-start'>
          <Form.Control
            type='text'
            name='followerArticleDisplayname'
            value={followerArticleDisplayname}
            disabled={true}
          />
          <ArticleSelect
            setSelectedArticleId={setSelectedFollowerArticleId}
            handleArticleWasChanged={handleArticleWasChanged}
            disabled={!articleBaseObj.isObsolet}
          />
        </div>
      </Form.Group>
    )
  }
  const renderAttributesData = () => {
    return (
      <>
        <StyledCollapseTrigger
          onClick={() => handleClickCollapse(setAttributesIsOpened)}
        >
          <h2 className='h1'>
            <ArrowBigIcon
              variant={attributesIsOpened ? 'down' : 'right'}
              color={colors.textColor}
            />
            {t('articleAdmin:attributesData')}
          </h2>
        </StyledCollapseTrigger>
        <Collapse in={attributesIsOpened}>
          <div>
            <Row className='mb-3'>
              <Form.Group as={Row}>
                <Form.Label column>{t('articleAdmin:unit')}</Form.Label>
                <Col>
                  {loadingStates.units ? (
                    <div className='d-flex align-items-center justify-content-center'>
                      <Spinner animation='border' variant='primary' />
                    </div>
                  ) : (
                    <ArraySelect
                      value={articleBaseObj.class.unit.id}
                      options={units}
                      noAutoSelect
                      name='classunit'
                      onChange={updateInputFields}
                      defaultOptionIsSelectable={true}
                      returnEvent
                    />
                  )}
                </Col>
              </Form.Group>
              <Col>
                <Form.Group className='d-block'>
                  <Form.Check
                    type='checkbox'
                    id={`isWithSizes-${articleBaseObj.id}`}
                    name='classisWithSizes'
                    label={t('articleAdmin:isWithSizes')}
                    checked={articleBaseObj.class.isWithSizes}
                    onChange={updateInputFields}
                    isInvalid={!!getFormError('classisWithSizes')}
                  />
                  {!!getFormError('classisWithSizes') && (
                    <StyledValidationMessage>
                      {getFormError('classisWithSizes')}
                    </StyledValidationMessage>
                  )}
                  {articleBaseObj.useRequestedEntitlementBookings === null ? null :
                    <>
                      <Form.Check
                        type='checkbox'
                        id={`useRequestedEntitlementBookings-${articleBaseObj.id}`}
                        name='useRequestedEntitlementBookings'
                        label={t('articleAdmin:useRequestedEntitlementBookings')}
                        checked={articleBaseObj.useRequestedEntitlementBookings}
                        onChange={updateInputFields}
                        isInvalid={!!getFormError('useRequestedEntitlementBookings')}
                      />
                      {!!getFormError('useRequestedEntitlementBookings') && (
                        <StyledValidationMessage>
                          {getFormError('useRequestedEntitlementBookings')}
                        </StyledValidationMessage>
                      )}
                    </>
                  }
                  <Form.Check
                    type='checkbox'
                    id={`isLocked-${articleBaseObj.id}`}
                    name='isLocked'
                    label={t('articleAdmin:isLocked')}
                    checked={articleBaseObj.isLocked}
                    onChange={updateInputFields}
                    isInvalid={!!getFormError('isLocked')}
                  />
                  {!!getFormError('isLocked') && (
                    <StyledValidationMessage>
                      {getFormError('isLocked')}
                    </StyledValidationMessage>
                  )}
                  <Form.Check
                    className='mb-3'
                    type='checkbox'
                    id={`isObsolet-${articleBaseObj.id}`}
                    name='isObsolet'
                    label={t('articleAdmin:isObsolet')}
                    checked={articleBaseObj.isObsolet}
                    onChange={updateInputFields}
                    isInvalid={!!getFormError('isObsolet')}
                  />
                  {!!getFormError('isObsolet') && (
                    <StyledValidationMessage>
                      {getFormError('isObsolet')}
                    </StyledValidationMessage>
                  )}
                  {renderFollowerArticle()}
                </Form.Group>
                <Form.Group className='d-block'>
                  <Form.Label className='d-block w-100'>
                    {t('articleAdmin:genderSubTitle')}
                  </Form.Label>
                  <Form.Check
                    type='checkbox'
                    id={`isAvailableMale-${articleBaseObj.id}`}
                    name='isAvailableMale'
                    label={t('articleAdmin:isAvailableMale')}
                    checked={articleBaseObj.isAvailableMale}
                    onChange={updateInputFields}
                    isInvalid={!!getFormError('isAvailableMale')}
                  />
                  {!!getFormError('isAvailableMale') && (
                    <StyledValidationMessage>
                      {getFormError('isAvailableMale')}
                    </StyledValidationMessage>
                  )}
                  <Form.Check
                    type='checkbox'
                    id={`isAvailableFeMale-${articleBaseObj.id}`}
                    name='isAvailableFeMale'
                    label={t('articleAdmin:isAvailableFeMale')}
                    checked={articleBaseObj.isAvailableFeMale}
                    onChange={updateInputFields}
                    isInvalid={!!getFormError('isAvailableFeMale')}
                  />
                  {!!getFormError('isAvailableFeMale') && (
                    <StyledValidationMessage>
                      {getFormError('isAvailableFeMale')}
                    </StyledValidationMessage>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Collapse>
      </>
    )
  }
  const renderVariousData = () => {
    return (
      <>
        <StyledCollapseTrigger
          onClick={() => handleClickCollapse(setVariousIsOpened)}
        >
          <h2 className='h1'>
            <ArrowBigIcon
              variant={variousIsOpened ? 'down' : 'right'}
              color={colors.textColor}
            />
            {t('articleAdmin:miscSubTitle')}
          </h2>
        </StyledCollapseTrigger>
        <Collapse in={variousIsOpened}>
          <div className='spacing-down'>
            <Row className='mb-3'>
              <Form.Group as={Row}>
                <Form.Label column>
                  {`${t('articleAdmin:pricePerItem')} (${articleBaseObj.price?.currency?.isoCode ?? ''
                    })`}
                </Form.Label>
                <Col>
                  <OverlaySpinnerWrapper>
                    <Form.Control
                      id={`price-${articleBaseObj.id}`}
                      value={articleBaseObj.price?.value ?? ''}
                      name='price'
                      type='number'
                      min='0'
                      onWheel={(e) => e.target.blur()}
                      onChange={updateInputFields}
                      isInvalid={!!getFormError('price')}
                    />
                    <Form.Control.Feedback type='invalid'>
                      {getFormError('price')}
                    </Form.Control.Feedback>
                  </OverlaySpinnerWrapper>
                </Col>
              </Form.Group>
            </Row>
            <Row className='mb-3'>
              <Form.Group as={Row}>
                <Form.Label column>
                  {t('articleAdmin:costPerItemPoints')}
                </Form.Label>
                <Col>
                  <OverlaySpinnerWrapper>
                    <Form.Control
                      id={`contingentsCostperItem-${articleBaseObj.id}`}
                      value={articleBaseObj.contingentsCostperItem}
                      name='contingentsCostperItem'
                      type='number'
                      min='0'
                      onWheel={(e) => e.target.blur()}
                      onChange={updateInputFields}
                      isInvalid={!!getFormError('contingentsCostperItem')}
                    />
                    <Form.Control.Feedback type='invalid'>
                      {getFormError('contingentsCostperItem')}
                    </Form.Control.Feedback>
                  </OverlaySpinnerWrapper>
                </Col>
              </Form.Group>
            </Row>
            <Row className='mb-3'>
              <Form.Group as={Row}>
                <Form.Label column>{t('articleAdmin:packageSize')}</Form.Label>
                <Col>
                  <OverlaySpinnerWrapper>
                    <Form.Control
                      id={`packageSize-${articleBaseObj.id}`}
                      value={articleBaseObj.packageSize}
                      name='packageSize'
                      type='number'
                      min='0'
                      onWheel={(e) => e.target.blur()}
                      onChange={updateInputFields}
                      isInvalid={!!getFormError('packageSize')}
                    //pattern='[0-9]'
                    />
                    <Form.Control.Feedback type='invalid'>
                      {getFormError('packageSize')}
                    </Form.Control.Feedback>
                  </OverlaySpinnerWrapper>
                </Col>
              </Form.Group>
            </Row>
          </div>
        </Collapse>
      </>
    )
  }
  const renderSupplyChainData = () => {
    return (
      <>
        <StyledCollapseTrigger
          onClick={() => handleClickCollapse(setSupplyChainIsOpened)}
        >
          <h2 className='h1'>
            <ArrowBigIcon
              variant={supplyChainIsOpened ? 'down' : 'right'}
              color={colors.textColor}
            />
            {t('articleAdmin:miscSubSupplyChain')}
          </h2>
        </StyledCollapseTrigger>
        <Collapse in={supplyChainIsOpened}>
          <div className='spacing-down'>
            <Row className='mb-3'>
              <Form.Group as={Row}>
                <Form.Label column>{t('articleAdmin:storehouse')}</Form.Label>
                <Col>
                  {loadingStates.storehouses ? (
                    <div className='d-flex align-items-center justify-content-center'>
                      <Spinner animation='border' variant='primary' />
                    </div>
                  ) : (
                    <ArraySelect
                      value={articleBaseObj.storehouse.id}
                      options={storehouses}
                      noAutoSelect
                      name='storehouse'
                      onChange={updateInputFields}
                      defaultOptionIsSelectable={true}
                      returnEvent
                    />
                  )}
                </Col>
              </Form.Group>
              <Form.Group as={Row}>
                <Form.Label column>{t('articleAdmin:supplier')}</Form.Label>
                <Col>
                  {loadingStates.suppliers ? (
                    <div className='d-flex align-items-center justify-content-center'>
                      <Spinner animation='border' variant='primary' />
                    </div>
                  ) : (
                    <ArraySelect
                      value={articleBaseObj.supplier.id}
                      options={suppliers}
                      noAutoSelect
                      name='supplier'
                      onChange={updateInputFields}
                      defaultOptionIsSelectable={true}
                      returnEvent
                    />
                  )}
                </Col>
              </Form.Group>
              <Form.Group as={Row}>
                <Form.Label column>
                  {t('articleAdmin:supplierAlias')}
                </Form.Label>
                <Col>
                  {loadingStates.supplierAlias ? (
                    <div className='d-flex align-items-center justify-content-center'>
                      <Spinner animation='border' variant='primary' />
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        id={`supplierAlias-${articleBaseObj.id}`}
                        name='supplierAlias'
                        placeholder={t('articleAdmin:supplierAlias')}
                        value={articleBaseObj.supplierAlias ?? ''}
                        onChange={updateInputFields}
                        isInvalid={!!getFormError('supplierAlias')}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {getFormError('supplierAlias')}
                      </Form.Control.Feedback>
                    </>
                  )}
                </Col>
              </Form.Group>
            </Row>
          </div>
        </Collapse>
      </>
    )
  }

  if (loadingStates.getArticleBaseData) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner animation='border' variant='primary' />
      </div>
    )
  }

  return (
    <div>
      {renderBaseData()}
      <Row className='mb-3'>{renderAttributesData()}</Row>
      <Row className='mb-3'>{renderVariousData()}</Row>
      <Row className='mb-3'>{renderSupplyChainData()}</Row>
      <OverlayContainer>
        <OverlayRight
          visible={overlayInternalNameIsVisible}
          onClose={handleCloseOverlay}
        >
          <LanguageForm
            languageTitle={t('articleAdmin:articleNameIntern')}
            handleCloseOverlay={handleCloseOverlay}
            languageArray={articleBaseObj.internalName.values}
            onChange={updateTranslations('internalName')}
          />
        </OverlayRight>
        <OverlayRight
          visible={overlayExternalNameIsVisible}
          onClose={handleCloseOverlay}
        >
          <LanguageForm
            languageTitle={t('articleAdmin:articleNameExtern')}
            handleCloseOverlay={handleCloseOverlay}
            languageArray={articleBaseObj.externalName.values}
            onChange={updateTranslations('externalName')}
          />
        </OverlayRight>
        <OverlayRight
          visible={overlayDescriptionIsVisible}
          onClose={handleCloseOverlay}
        >
          <LanguageForm
            inputType='textarea'
            languageTitle={t('articleAdmin:description')}
            handleCloseOverlay={handleCloseOverlay}
            languageArray={articleBaseObj.description.values}
            onChange={updateTranslations('internalName')}
          />
        </OverlayRight>
        <OverlayRight
          visible={overlayCommntIsVisible}
          onClose={handleCloseOverlay}
        >
          <LanguageForm
            inputType='textarea'
            languageTitle={t('articleAdmin:comment')}
            handleCloseOverlay={handleCloseOverlay}
            languageArray={articleBaseObj.comment.values}
            onChange={updateTranslations('comment')}
          />
        </OverlayRight>
      </OverlayContainer>
    </div>
  )
}

export default ArticleMainForm
